import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { IconCross } from '@xing-com/icons';
import { ProgressBar } from '@xing-com/progress-bar';
import { AbsoluteTimestamp } from '@xing-com/timestamp';
import {
  scale130,
  scale230,
  spaceL,
  spaceM,
  xdlColorText,
  xdlColorBackgroundTertiary,
} from '@xing-com/tokens';
import { BodyCopy, Meta } from '@xing-com/typography';

import { FILE_ICONS_BY_TYPE, DEFAULT_FILE_ICON } from './helpers';
import type { FileUpload } from './types';

type Props = {
  fileUpload?: FileUpload;
  onDelete: () => void;
};

export const FileItem: React.FC<Props> = ({ fileUpload, onDelete }) => {
  const { formatMessage } = useIntl();

  if (!fileUpload || fileUpload.status === 'error') {
    return null;
  }
  const isUploadInProgress =
    fileUpload?.status === 'loading-token' ||
    fileUpload?.status === 'uploading';

  const { name, type, size } = fileUpload.file;
  const { progress, lastUsed, url } = fileUpload;
  const Icon = FILE_ICONS_BY_TYPE[type] ?? DEFAULT_FILE_ICON;
  const onFileItemClick = (): void => {
    if (url) {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  const FileInfo = (
    <>
      <IconContainer>
        <Icon aria-hidden />
      </IconContainer>

      <BodyCopyContainer>
        <StyledBodyCopy fontWeight="bold" size="medium" noMargin>
          {name}
        </StyledBodyCopy>
        <StyledMeta size="small">
          {lastUsed
            ? formatMessage(
                {
                  id: 'JOBS_INSTANT_APPLY_FORM_FILE_LAST_USED',
                },
                {
                  lastUsed: (
                    <AbsoluteTimestamp date={lastUsed} variant="longDateFull" />
                  ),
                }
              )
            : formatBytes(size, 0)}
        </StyledMeta>
      </BodyCopyContainer>
    </>
  );

  return (
    <FileContainer>
      {isUploadInProgress ? (
        <StyledProgressbar
          progress={progress}
          data-testid="upload-progress-bar"
        />
      ) : url ? (
        <ClickableInfo
          role="button"
          tabIndex={0}
          onClick={onFileItemClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onFileItemClick();
            }
          }}
        >
          {FileInfo}
        </ClickableInfo>
      ) : (
        FileInfo
      )}
      <StyledButton
        icon={IconCross}
        onClick={onDelete}
        data-testid="delete-upload-button"
        aria-label={formatMessage(
          {
            id: 'JOBS_INSTANT_APPLY_FORM_DELETE_FILE_UPLOAD_BUTTON_A11Y_WEB',
          },
          { fileName: name }
        )}
      />
    </FileContainer>
  );
};

export const FileContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${spaceL};
  background: ${xdlColorBackgroundTertiary};
  padding: ${spaceM};
`;

export const StyledProgressbar = styled(ProgressBar)`
  padding: ${spaceM} ${spaceM} 0 0;
  width: 100%;
`;

export const BodyCopyContainer = styled.div`
  overflow-wrap: break-word;
  width: calc(100% - ${scale230});
`;

export const ClickableInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spaceL};
  flex: 1;
  color: ${xdlColorText};
  text-decoration: none;
  cursor: pointer;
`;

export const StyledBodyCopy = styled(BodyCopy)`
  word-break: break-word;

  ${lineClamp(2)}
`;

export const IconContainer = styled.div`
  height: ${scale130};
  min-width: ${scale130};
  width: ${scale130};
`;

export const StyledButton = styled(Button)`
  height: auto;
`;

export const StyledMeta = styled(Meta)`
  margin: 0;
  && {
    color: ${xdlColorText};
  }
`;
